import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { ModalService } from '@epicuro-next/ataraxia/modal';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import { environmentSettings } from '@epicuro-next/platform/environment';
import { Severity } from 'graphql-api/baskets-api-interfaces';
import { BehaviorSubject, map } from 'rxjs';

import { IMessage } from '../../models/message.interface';
import { SupportComponent } from '../support/support.component';
import { AuthTermsComponent } from '../terms/terms.component';

import { LoginComponentStore } from './login.component-store';

const defaultErrorMessage = $localize`:AuthPage|Login failed message@@loginPageLoginFailedMessage:The user name or password is incorrect.`;
const LOGIN_ERROR_MESSAGES: { [key: string]: string } = {
  invalid_grant: defaultErrorMessage,
  user_lockout: $localize`:AuthPage|User lockout message@@loginPageUserLockoutMessage:Due to too many failed login attempts your user account has been locked. Please contact the following email address to get support: fasttrackhelp@brooksrunning.com`,
};

@Component({
  selector: 'epc-auth-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [LoginComponentStore],
  standalone: false,
})
export class LoginComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private modalService = inject(ModalService);
  private merchandiseService = inject(MerchandiseService);
  private destroy$ = inject(DestroyRef);
  private componentStore = inject(LoginComponentStore);

  public severity = Severity;
  public get needToAgreeToTerms() {
    try {
      const auth: { needToAgreeToTerms: boolean } = JSON.parse(
        environmentSettings.auth,
      );

      return auth.needToAgreeToTerms;
    } catch {
      return false;
    }
  }
  public get termsChecked() {
    return !!this.loginForm.value.terms;
  }
  public get queryParams() {
    const userId = this.loginForm.value.username;

    if (!userId) {
      return {};
    }

    return {
      userId,
    };
  }
  public loginForm = this.fb.group({
    username: ['', { validators: [Validators.required] }],
    password: ['', { validators: [Validators.required] }],
    ...(this.needToAgreeToTerms
      ? { terms: [false, { validators: [Validators.requiredTrue] }] }
      : {}),
  });
  public message$ = new BehaviorSubject<IMessage | null>(null);
  public passwordVisible$ = new BehaviorSubject<boolean>(false);
  public loginFailedError$ = this.componentStore.error$.pipe(
    map((error) => {
      if (error) {
        return LOGIN_ERROR_MESSAGES[error] ?? defaultErrorMessage;
      }

      return null;
    }),
  );

  public passwordTooltip$ = this.passwordVisible$.pipe(
    map((visible) => {
      if (!visible) {
        return $localize`:AuthPage|Show password tooltip value@@loginPageShowPasswordTooltip:Show password`;
      }

      return $localize`:AuthPage|Hide password tooltip value@@loginPageHidePasswordTooltip:Hide password`;
    }),
    takeUntilDestroyed(this.destroy$),
  );

  public ngOnInit(): void {
    this.getMessages();
  }

  public loginSubmit() {
    if (this.loginForm.invalid) {
      return;
    }

    const formValue = this.loginForm.value;

    this.componentStore.login(
      formValue.username as string,
      formValue.password as string,
    );
  }

  public toggleTerms() {
    const currentValue = this.loginForm.value.terms;

    this.loginForm.patchValue(
      {
        terms: !currentValue,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // tslint:disable-next-line:no-any
      } as any,
      { emitEvent: false },
    );
  }

  public showTermsModal() {
    this.modalService.open(AuthTermsComponent, {
      size: 'xl',
      backdrop: true,
      scrollable: true,
    });
  }

  public openSupportModal() {
    this.modalService.open(SupportComponent, {
      size: 'xl',
      backdrop: true,
      scrollable: true,
    });
  }

  public togglePasswordType() {
    this.passwordVisible$.next(!this.passwordVisible$.getValue());
  }

  private getMessages() {
    this.merchandiseService
      .getMessages()
      .pipe(
        map((response) => {
          return this.merchandiseService.parseResponse(response) as IMessage;
        }),
        takeUntilDestroyed(this.destroy$),
      )
      .subscribe((message: IMessage) => {
        this.message$.next(message);
      });
  }
}
